// CreditsStatusModal.tsx
import React from 'react';
import { Modal, Button } from 'antd';
import {
	CreditCardOutlined,
	AutorenewOutlined,
	CallOutlined,
	SmsOutlined,
} from '@mui/icons-material';

interface CreditsStatusModalProps {
	isVisible: boolean;
	onClose: () => void;
	onNavigateToCredits: () => void;
	voiceCredits: number;
	smsCredits: number;
	voiceAutoReloadEnabled: boolean;
	smsAutoReloadEnabled: boolean;
	selectedCreditType: 'voice' | 'sms' | 'both';
}

const CreditsStatusModal: React.FC<CreditsStatusModalProps> = ({
	isVisible,
	onClose,
	onNavigateToCredits,
	voiceCredits,
	smsCredits,
	voiceAutoReloadEnabled,
	smsAutoReloadEnabled,
	selectedCreditType,
}) => {
	// Handle enabling auto-reload
	const handleEnableAutoReload = () => {
		onNavigateToCredits();
	};

	const renderModalContent = () => {
		// Always show both voice and SMS credits in the modal
		// The selectedCreditType is only used for warning emphasis

		return (
			<div className="space-y-6 p-2">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* Voice Credits Card - Always Show */}
					<div
						className={`bg-white rounded-2xl border ${selectedCreditType === 'voice' || (selectedCreditType === 'both' && !voiceAutoReloadEnabled && voiceCredits <= 50) ? 'border-blue-200' : 'border-gray-100'} p-6 shadow-lg transition-all group`}
					>
						<div className="flex flex-col space-y-4">
							<div className="flex justify-between items-center">
								<div className="flex items-center space-x-4">
									<div className="p-3.5 bg-blue-50 rounded-2xl group-hover:bg-blue-100 transition-colors">
										<CallOutlined
											className="text-blue-600 group-hover:text-blue-700 transition-colors"
											style={{ fontSize: '24px' }}
										/>
									</div>
									<div>
										<h3 className="text-sm font-semibold text-gray-700 mb-1">Voice Credits</h3>
										<p className="text-xs text-gray-500">Current Balance</p>
									</div>
								</div>
								<div className="text-right">
									<span
										className={`text-xl font-bold tracking-tight ${
											voiceCredits <= 20 ? 'text-red-600 animate-pulse' : 'text-gray-800'
										}`}
									>
										{voiceCredits}
									</span>
								</div>
							</div>

							<div className="border-t border-gray-100 pt-4">
								<div className="flex justify-between items-center">
									<span className="text-xs text-gray-600">Auto-reload Status</span>
									<div
										className={`text-xs font-medium flex items-center space-x-2 ${
											voiceAutoReloadEnabled ? 'text-green-600' : 'text-red-600'
										}`}
									>
										<span
											className={`w-2.5 h-2.5 rounded-full ${voiceAutoReloadEnabled ? 'bg-green-500' : 'bg-red-500'}`}
										/>
										<span>{voiceAutoReloadEnabled ? 'Enabled' : 'Disabled'}</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* SMS Credits Card - Always Show */}
					<div
						className={`bg-white rounded-2xl border ${selectedCreditType === 'sms' || (selectedCreditType === 'both' && !smsAutoReloadEnabled && smsCredits <= 15) ? 'border-green-200' : 'border-gray-100'} p-6 shadow-lg transition-all group`}
					>
						<div className="flex flex-col space-y-4">
							<div className="flex justify-between items-center">
								<div className="flex items-center space-x-4">
									<div className="p-3.5 bg-green-50 rounded-2xl group-hover:bg-green-100 transition-colors">
										<SmsOutlined
											className="text-green-600 group-hover:text-green-700 transition-colors"
											style={{ fontSize: '24px' }}
										/>
									</div>
									<div>
										<h3 className="text-sm font-semibold text-gray-700 mb-1">SMS Credits</h3>
										<p className="text-xs text-gray-500">Current Balance</p>
									</div>
								</div>
								<div className="text-right">
									<span
										className={`text-xl font-bold tracking-tight ${
											smsCredits <= 20 ? 'text-red-600 animate-pulse' : 'text-gray-800'
										}`}
									>
										{smsCredits}
									</span>
								</div>
							</div>

							<div className="border-t border-gray-100 pt-4">
								<div className="flex justify-between items-center">
									<span className="text-xs text-gray-600">Auto-reload Status</span>
									<div
										className={`text-xs font-medium flex items-center space-x-2 ${
											smsAutoReloadEnabled ? 'text-green-600' : 'text-red-600'
										}`}
									>
										<span
											className={`w-2.5 h-2.5 rounded-full ${smsAutoReloadEnabled ? 'bg-green-500' : 'bg-red-500'}`}
										/>
										<span>{smsAutoReloadEnabled ? 'Enabled' : 'Disabled'}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{((!voiceAutoReloadEnabled && voiceCredits <= 20) ||
					(!smsAutoReloadEnabled && smsCredits <= 20)) && (
					<div className="bg-amber-50/70 border border-amber-200/50 rounded-2xl p-6">
						<div className="flex items-center space-x-5">
							<div className="text-4xl opacity-80">⚠️</div>
							<div className="flex-1">
								<h4 className="text-base font-semibold text-amber-900 mb-3">Low Credits Warning</h4>
								<div className="space-y-2.5">
									{!voiceAutoReloadEnabled && voiceCredits <= 20 && (
										<div className="flex items-center text-amber-800">
											<span className="w-2.5 h-2.5 bg-amber-500 rounded-full mr-3" />
											<span className="text-xs">
												Voice calls may experience interruptions due to low credits
											</span>
										</div>
									)}
									{!smsAutoReloadEnabled && smsCredits <= 20 && (
										<div className="flex items-center text-amber-800">
											<span className="w-2.5 h-2.5 bg-amber-500 rounded-full mr-3" />
											<span className="text-xs">
												SMS messages might fail to send due to low credits
											</span>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	};

	return (
		<Modal
			title={
				<div className="flex items-center gap-2">
					<div className="p-2 bg-blue-50 rounded-full">
						<CreditCardOutlined className="text-blue-600" style={{ fontSize: '18px' }} />
					</div>
					<span className="font-semibold text-gray-900">Credits Status</span>
				</div>
			}
			open={isVisible}
			onCancel={onClose}
			footer={[
				<Button
					key="auto-reload"
					type="primary"
					icon={<AutorenewOutlined />}
					onClick={handleEnableAutoReload}
					className="bg-blue-600 hover:bg-blue-700"
				>
					Enable Auto-reload
				</Button>,
				<Button
					key="add-credits"
					onClick={onNavigateToCredits}
					className="border-blue-600 text-blue-600 hover:bg-blue-50"
				>
					Add Credits
				</Button>,
			]}
			width={650}
			className="credits-status-modal"
		>
			{renderModalContent()}
		</Modal>
	);
};

export default CreditsStatusModal;
